import { ProjectFormData, ProjectFormSaveInfo } from './types';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FieldNamesMarkedBoolean,
  Resolver,
  useForm,
  useFormContext,
} from 'react-hook-form';
import * as yup from 'yup';
import i18next from 'i18next';
import { mapValues } from 'lodash';
import { useCallback, useState } from 'react';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import { useUpdateProject } from './useProjectMutations';

const i18prefix = 'timeTracking.projects.form';

type ProjectValidationData = Pick<
  ProjectFormData,
  'name' | 'estimation' | 'endDate'
>;

const validationSchema: yup.ObjectSchema<ProjectValidationData> = yup.object({
  name: yup.string().max(255).required(),
  estimation: yup.number().integer().nullable().defined(),
  endDate: yup
    .date()
    .nullable()
    .defined()
    .min(
      yup.ref('startDate'),
      i18next.t(`${i18prefix}.validationEndDateShouldBeGreater`),
    ),
});

export const useUpdateProjectForm = (values: ProjectFormData) => {
  const form = useForm<ProjectFormData>({
    defaultValues: values,
    mode: 'onChange',
    shouldUnregister: false,
    resolver: yupResolver(
      validationSchema,
    ) as unknown as Resolver<ProjectFormData>,
  });

  return form;
};

export const useSubmitUpdate = () => {
  const { handleSubmit: handleFormSubmit, reset: formReset } =
    useFormContext<ProjectFormData>();
  const errorHandler = useMutationErrorHandler();
  const [saveInfo, setSaveInfo] = useState<ProjectFormSaveInfo>({});

  const [updateProject] = useUpdateProject();

  const submitUpdate = useCallback(
    (
      projectId: string,
      fieldsToUpdate: Partial<
        Readonly<FieldNamesMarkedBoolean<ProjectFormData>>
      >,
    ) => {
      handleFormSubmit((formData: ProjectFormData) => {
        setSaveInfo(createSavingInfo(fieldsToUpdate));

        return updateProject(projectId, formData)
          .then(() => {
            setSaveInfo((prev) => setCompleted(prev));
          })
          .catch((e) => {
            formReset();
            setSaveInfo((prev) => setCompleted(prev, false));
            errorHandler(e);
          });
      })();
    },
    [errorHandler, formReset, handleFormSubmit, updateProject, setSaveInfo],
  );

  return { submitUpdate, saveInfo };
};

const createSavingInfo = (
  fields: Partial<Readonly<FieldNamesMarkedBoolean<ProjectFormData>>>,
): Partial<ProjectFormSaveInfo> => mapValues(fields, () => ({ loading: true }));

const setCompleted = (
  savingInfo: Partial<ProjectFormSaveInfo>,
  completed: boolean = true,
) => mapValues(savingInfo, () => ({ loading: false, completed }));
